'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { Text } from '@/components/ui/text/Text';
import { TextTag } from '@/components/ui/text/Text.types';
import { Colors } from '@/lib/colorUtils';

/**
 * Layout and accessibility wrapper for form fields.
 *
 * Responsibilities:
 * - Render label and required marker.
 * - Optionally show an info tooltip next to the label.
 * - Wrap the actual form control as `children`.
 * - Render helper or error text, wired up via `aria-describedby` / `role="alert"`.
 *
 * ## Invariants:
 * - The string-form `infoTooltip` bubble is rendered via `createPortal` to
 *   `document.body` with `position: fixed`. Why: the wrapper is frequently
 *   placed inside ancestors with `overflow: hidden` (e.g. animated grid-row
 *   tab panels in `CouponReservationSection`), and an in-flow absolute
 *   bubble would be clipped at the nearest clipping ancestor — see PB-729.
 *   Do not refactor the bubble back into an inline-positioned child without
 *   first removing every clipping ancestor in the call sites.
 */
export interface FieldWrapperProps {
  /** Text label associated with the field. */
  label?: string;

  /** When true, shows a required `*` marker next to the label. */
  required?: boolean;

  /**
   * When true, shows the same required `*` marker as `required`, but purely as
   * a visual cue — it does NOT imply native HTML `required` validation.
   * Use for "at least one of these fields is required" groups (e.g. mobile OR
   * email), where marking each field natively `required` would wrongly force
   * both to be filled.
   */
  requiredMarker?: boolean;

  /**
   * Optional information displayed next to the label.
   * - string → rendered as an icon with a hover tooltip (portaled to body).
   * - ReactNode → rendered directly (gives full control to the caller).
   */
  infoTooltip?: string | ReactNode;

  /** Current error message, if any. When set, it takes precedence over `helperText`. */
  error?: string | null;

  /** Helper text shown when there is no error. */
  helperText?: string;

  /** ID of the associated form field; used to wire `htmlFor` and helper text. */
  id: string;

  /**
   * Optional ID used for the helper/error text element.
   * Falls back to `${id}-helper` when omitted.
   */
  helperId?: string;

  /** Extra class names applied to the outer wrapper container. */
  className?: string;

  /**
   * `data-cy` for the error message element. Defaults to `field-error`; a caller that needs a
   * field-specific selector (e.g. `home-lead-form-email-error`) passes its own. Additive only:
   * the helper text keeps `field-helper`.
   */
  errorDataCy?: string;

  /**
   * When true, removes the bottom padding reserved for helper/error text.
   * Helper/error text then renders in flow below the field (the wrapper grows
   * when it appears) instead of absolutely into the reserved space — without
   * this, the text overlapped the control itself (PB-4346).
   */
  hideBottomPadding?: boolean;

  /** Form control (e.g. `BaseInput`) rendered inside the wrapper. */
  children: ReactNode;
}

const TOOLTIP_GAP = 6;
const TOOLTIP_MAX_WIDTH = 220;
const VIEWPORT_PADDING = 8;
// Approximate flip threshold: if the trigger sits closer than this to the
// viewport top, place the bubble below it instead of above.
const FLIP_THRESHOLD = 60;

interface TooltipPosition {
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

export function FieldWrapper({
  label,
  required = false,
  requiredMarker = false,
  infoTooltip,
  error,
  helperText,
  id,
  helperId,
  className = '',
  errorDataCy = 'field-error',
  hideBottomPadding = false,
  children,
}: FieldWrapperProps) {
  const isStringTooltip = typeof infoTooltip === 'string';

  // Internal state that only controls visibility of the string-based info tooltip.
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  // Measure helper/error text height to dynamically adjust bottom padding
  const helperRef = useRef<HTMLParagraphElement>(null);
  const [helperHeight, setHelperHeight] = useState(0);

  useEffect(() => {
    const el = helperRef.current;
    if (!el) {
      setHelperHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => {
      setHelperHeight(el.scrollHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [error, helperText]);

  const computeTooltipPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === 'undefined') return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const halfWidth = TOOLTIP_MAX_WIDTH / 2;
    const centerX = rect.left + rect.width / 2;
    // Keep the bubble inside the viewport horizontally even when the trigger
    // is near an edge — `transform: translateX(-50%)` does the centering.
    const clampedLeft = Math.min(
      Math.max(centerX, halfWidth + VIEWPORT_PADDING),
      Math.max(viewportWidth - halfWidth - VIEWPORT_PADDING, halfWidth + VIEWPORT_PADDING)
    );
    const placement: 'top' | 'bottom' = rect.top > FLIP_THRESHOLD + TOOLTIP_GAP ? 'top' : 'bottom';
    const top = placement === 'top' ? rect.top - TOOLTIP_GAP : rect.bottom + TOOLTIP_GAP;
    setTooltipPosition({ top, left: clampedLeft, placement });
  }, []);

  // useLayoutEffect (not useEffect): run synchronously after the visibility
  // re-render, before paint, so the bubble is positioned in the same frame it
  // becomes visible — no one-frame gap between "infoTooltipVisible=true" and
  // "tooltipPosition computed". Component is 'use client' so SSR doesn't run
  // this hook.
  useLayoutEffect(() => {
    if (!isStringTooltip || !infoTooltipVisible) return;
    computeTooltipPosition();
    const handle = () => computeTooltipPosition();
    // Capture-phase scroll listener so nested scroll containers also trigger reposition.
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [isStringTooltip, infoTooltipVisible, computeTooltipPosition]);

  const showTooltip = useCallback(() => {
    if (!isStringTooltip) return;
    setInfoTooltipVisible(true);
  }, [isStringTooltip]);

  const hideTooltip = useCallback(() => {
    if (!isStringTooltip) return;
    setInfoTooltipVisible(false);
    setTooltipPosition(null);
  }, [isStringTooltip]);

  // Helper/error element ID, used together with `aria-describedby` from the input element.
  const resolvedHelperId = helperId ?? `${id}-helper`;
  const hasError = Boolean(error);

  const tooltipPortal =
    isStringTooltip && infoTooltipVisible && tooltipPosition && typeof document !== 'undefined'
      ? createPortal(
          <Text
            tag={TextTag.span}
            size="12"
            color={Colors.colorBrandWhite}
            className="tw:pointer-events-none tw:fixed tw:z-50 tw:rounded tw:bg-gray-darker tw:px-2 tw:py-1 tw:whitespace-normal tw:break-words"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              maxWidth: TOOLTIP_MAX_WIDTH,
              transform:
                tooltipPosition.placement === 'top'
                  ? 'translate(-50%, -100%)'
                  : 'translate(-50%, 0)',
            }}
            role="tooltip"
          >
            {infoTooltip as string}
          </Text>,
          document.body
        )
      : null;

  return (
    <div
      className={`tw:min-w-0 tw:w-full tw:flex tw:flex-col tw:gap-[var(--spacing-micro)] tw:relative ${hideBottomPadding ? '' : 'tw:pb-[var(--spacing-large)]'} ${className}`.trim()}
      // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (measured helper-text height)
      style={!hideBottomPadding && helperHeight > 20 ? { paddingBottom: helperHeight } : undefined}
    >
      {label != null && (
        <div className="tw:flex tw:items-center tw:gap-[var(--spacing-micro)]">
          <Text
            tag={TextTag.label}
            htmlFor={id}
            size="14"
            color={hasError ? Colors.colorBrandError : Colors.colorGrayDarker}
          >
            {label}
            {(required || requiredMarker) && (
              <Text
                tag={TextTag.span}
                size="14"
                color={Colors.colorBrandError}
                className="tw:ml-0.5"
                aria-hidden="true"
              >
                *
              </Text>
            )}
          </Text>

          {infoTooltip != null && (
            <span
              ref={isStringTooltip ? triggerRef : undefined}
              className="tw:flex tw:items-center tw:justify-center tw:text-gray-dark"
              // For string tooltips we expose an accessible description on hover icon.
              role={isStringTooltip ? 'img' : undefined}
              aria-label={isStringTooltip ? (infoTooltip as string) : 'More information'}
              onMouseEnter={isStringTooltip ? showTooltip : undefined}
              onMouseLeave={isStringTooltip ? hideTooltip : undefined}
              onFocus={isStringTooltip ? showTooltip : undefined}
              onBlur={isStringTooltip ? hideTooltip : undefined}
            >
              {isStringTooltip ? (
                <HiOutlineInformationCircle className="tw:h-4 tw:w-4" aria-hidden />
              ) : (
                infoTooltip
              )}
            </span>
          )}
        </div>
      )}

      {children}

      {/* Helper and error text share the same DOM node to keep `aria-describedby` stable.
          With hideBottomPadding there is no reserved space to absolutely position into,
          so the text flows below the control (the column gap provides the spacing) —
          absolute positioning there overlapped the control itself (PB-4346). */}
      {(helperText != null || error != null) && (
        <Text
          ref={helperRef}
          id={resolvedHelperId}
          tag={TextTag.p}
          size="12"
          color={hasError ? Colors.colorBrandError : Colors.colorGrayDarker}
          className={`tw:leading-tight ${hideBottomPadding ? '' : 'tw:absolute tw:bottom-0 tw:left-0 tw:right-0'}`}
          // When in error state, announce changes to assistive technologies.
          role={hasError ? 'alert' : undefined}
          data-cy={hasError ? errorDataCy : 'field-helper'}
        >
          {hasError ? error : helperText}
        </Text>
      )}

      {tooltipPortal}
    </div>
  );
}
